import React, { useState, useEffect } from 'react';
import llmService from '../../services/llmService';
import './ApiKeyModal.css';

function ApiKeyModal({ isOpen, onClose, onSetupComplete }) {
  const [availableLLMs, setAvailableLLMs] = useState([]);
  const [selectedLLM, setSelectedLLM] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [customName, setCustomName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [userLLMs, setUserLLMs] = useState([]);
  const [showDetailedSteps, setShowDetailedSteps] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      console.log('📋 Loading available LLMs...');
      const [llmsResponse, userResponse] = await Promise.all([
        llmService.getAvailableLLMs(),
        llmService.getUserLLMs(),
      ]);

      console.log('✅ Available LLMs:', llmsResponse.llms);
      setAvailableLLMs(llmsResponse.llms || []);
      setUserLLMs(userResponse.userLLMs || []);

      if (llmsResponse.llms?.length && !selectedLLM) {
        setSelectedLLM(llmsResponse.llms[0].id);
        console.log('🎯 Default LLM selected:', llmsResponse.llms[0].name);
      }
    } catch (error) {
      console.error('❌ Failed to load data:', error);
      setMessage({ type: 'error', text: `Failed to load data: ${error.message}` });
      setAvailableLLMs([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedLLM || !apiKey.trim()) {
      setMessage({ type: 'error', text: 'Please select an LLM and enter an API key.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      console.log('🔐 Validating API key for:', selectedLLM);
      const result = await llmService.validateApiKey(selectedLLM, apiKey, customName);
      setMessage({ type: 'success', text: result.message || 'API key saved successfully.' });

      // Reset fields and reload
      setApiKey('');
      setCustomName('');
      await loadData();

      // Check if setup complete
      const requirement = await llmService.checkApiKeyRequirement();
      if (requirement.hasApiKeys) {
        setTimeout(() => {
          if (onSetupComplete) onSetupComplete();
        }, 1500);
      }
    } catch (error) {
      console.error('❌ API key validation failed:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedLLM = () => {
    return availableLLMs.find((llm) => llm.id === selectedLLM) || {};
  };

  const handleClose = () => {
    setMessage({ type: '', text: '' });
    setApiKey('');
    setCustomName('');
    onClose();
  };

  const renderGeminiSteps = () => {
    return (
      <div className="api-key-help detailed">
        <p><strong>📋 Complete Steps to Get Gemini API Key:</strong></p>
        
        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <strong>Go to Google AI Studio</strong>
              <p>Visit <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer">makersuite.google.com/app/apikey</a></p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <strong>Sign in with Google Account</strong>
              <p>Use your Google/Gmail account to log in</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <strong>Create a New API Key</strong>
              <p>Click on "Create API Key" button (usually blue button)</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <strong>Select Key Type</strong>
              <p>Choose "Create API key in new project" or use existing project</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">5</div>
            <div className="step-content">
              <strong>Name Your Key</strong>
              <p>Give your key a recognizable name (e.g., "PDF Analyzer Key")</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">6</div>
            <div className="step-content">
              <strong>Copy Your API Key</strong>
              <p>Copy the key and paste it here in its field above</p>
              
            </div>
          </div>

          
        </div>

        <div className="troubleshooting">
          <h4>🔧 Troubleshooting:</h4>
          <ul>
            <li><strong>Need billing setup?</strong> Visit <a href="https://console.cloud.google.com/billing" target="_blank" rel="noopener noreferrer">Google Cloud Billing</a></li>
            <li><strong>Free tier:</strong> Gemini API offers free usage within limits</li>
            <li><strong>Format:</strong> API key looks like: <code>AIzaSyD...example...key</code></li>
          </ul>
        </div>
      </div>
    );
  };

  const renderStandardHelp = () => {
    const selectedLLMObj = getSelectedLLM();
    const isGemini = selectedLLMObj.provider === 'Google' || selectedLLMObj.provider === 'Gemini';

    return (
      <div className="api-key-help">
        <p><strong>Where to get your API key:</strong></p>
        {isGemini ? (
          <>
            <ul>
              <li>
                Visit{' '}
                <a
                  href="https://makersuite.google.com/app/apikey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external-link"
                >
                  Google AI Studio
                </a>{' '}
                → Create API Key
              </li>
              <li>Your API key is encrypted and stored securely</li>
              <li>We only validate the key; it's never stored in plain text</li>
            </ul>
            <button 
              type="button" 
              className="show-steps-button"
              onClick={() => setShowDetailedSteps(true)}
            >
              📋 Show Detailed Step-by-Step Guide
            </button>
          </>
        ) : (
          <ul>
            <li>Visit the provider's website to create an account</li>
            <li>Navigate to API Keys/Developer section</li>
            <li>Generate a new API key with appropriate permissions</li>
            <li>Your API key is encrypted and stored securely</li>
          </ul>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  if (availableLLMs.length === 0 && !message.text) {
    return (
      <div className="modal-overlay">
        <div className="api-key-modal">
          <div className="modal-header">
            <h2>🔑 Configure AI Service</h2>
            <button className="close-button" onClick={handleClose}>×</button>
          </div>
          <div className="modal-content">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading available AI services...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="api-key-modal">
        <div className="modal-header">
          <h2>🔑 Configure AI Service</h2>
          <p>Add an API key to enable PDF analysis features</p>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="modal-content">
          {/* User's Configured LLMs */}
          {userLLMs.length > 0 && (
            <div className="current-configurations">
              <h3>Your Current Configurations</h3>
              <div className="llm-list">
                {userLLMs.map((llm) => (
                  <div key={llm.llmId} className="llm-config-item">
                    <span className="llm-name">{llm.customName}</span>
                    <span className={`status ${llm.isActive ? 'active' : 'inactive'}`}>
                      {llm.isActive ? '✅ Active' : '❌ Inactive'}
                      {llm.isDefault && ' (Default)'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Configuration */}
          <div className="add-configuration">
            <h3>{userLLMs.length > 0 ? '' : 'Add Your First Service'}</h3>

            <form onSubmit={handleSubmit} className="api-key-form">
              {/* LLM Selector */}
              <div className="form-group">
                <label htmlFor="llmSelect">AI Service *</label>
                <select
                  id="llmSelect"
                  value={selectedLLM}
                  onChange={(e) => {
                    setSelectedLLM(e.target.value);
                    setShowDetailedSteps(false);
                  }}
                  required
                  className="llm-select"
                >
                  <option value="">Select an AI service...</option>
                  {availableLLMs.map((llm) => (
                    <option key={llm.id} value={llm.id}>
                      {llm.name} ({llm.provider})
                    </option>
                  ))}
                </select>

                {selectedLLM && (
                  <div className="llm-description">
                    <small>{getSelectedLLM().description}</small>
                  </div>
                )}
              </div>

              {/* Custom Name Input */}
              <div className="form-group">
                <label htmlFor="customName">Configuration Name (Optional)</label>
                <input
                  id="customName"
                  type="text"
                  placeholder="e.g., 'Work API Key', 'Personal Gemini'"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="custom-name-input"
                />
                <small className="helper-text">Helps you identify this configuration later</small>
              </div>

              {/* API Key Input */}
              <div className="form-group">
                <label htmlFor="apiKey">API Key *</label>
                <input
                  id="apiKey"
                  type="password"
                  placeholder="Paste your API key here (starts with AIza... for Gemini)"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  required
                  className="api-key-input"
                />

                {showDetailedSteps ? renderGeminiSteps() : renderStandardHelp()}
              </div>

              {/* Status Message */}
              {message.text && (
                <div className={`message ${message.type}`}>{message.text}</div>
              )}

              {/* Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading || !selectedLLM || !apiKey.trim()}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner-small"></span>
                      Validating...
                    </>
                  ) : (
                    '✅ Save API Key'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiKeyModal;